import moment from 'moment-timezone';
import {
  Maybe,
  UserWorkingHour,
  WeeklyScheduleResult,
} from 'generated/graphql';

export type ScheduledTimeFrame = {
  start: string;
  end: string;
};

// Keys match the `days` i18n namespace; the day index matches the
// 0 = Sunday numbering used by UserWorkingHour.day.
export type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

// Ordered for display: Mon-Sat first, Sunday last.
const WEEKLY_SCHEDULE_DAYS: Array<[DayKey, number]> = [
  ['mon', 1],
  ['tue', 2],
  ['wed', 3],
  ['thu', 4],
  ['fri', 5],
  ['sat', 6],
  ['sun', 0],
];

const DISPLAY_ORDER = WEEKLY_SCHEDULE_DAYS.map(([, day]) => day);

const sortByDisplayOrder = (a: number, b: number) =>
  DISPLAY_ORDER.indexOf(a) - DISPLAY_ORDER.indexOf(b);

const isNumber = (value: unknown): value is number => typeof value === 'number';

// Server clock strings are 24h "HH:mm" or "HH:mm:ss".
const CLOCK_INPUT_FORMATS = ['HH:mm:ss', 'HH:mm'];

export const getScheduledDaysFromWeeklySchedule = (
  schedule: Maybe<Partial<WeeklyScheduleResult>> | undefined
): DayKey[] => {
  if (!schedule) {
    return [];
  }

  // WeeklyScheduleResult booleans use a true = in-office convention.
  return WEEKLY_SCHEDULE_DAYS.filter(([key]) => schedule[key] === true).map(
    ([key]) => key
  );
};

/**
 * Returns the earliest-in-week time frame from a user's working hours array,
 * or null if none exists. Days are ordered Mon–Sat first, then Sunday.
 */
export const getPrimaryTimeFrame = (
  workingHours: Maybe<Array<Maybe<UserWorkingHour>>> | undefined
): ScheduledTimeFrame | null => {
  if (!workingHours) {
    return null;
  }

  const orderedByDay = workingHours
    .filter((wh): wh is UserWorkingHour => wh !== null && wh !== undefined)
    .filter((wh) => isNumber(wh.day))
    .sort((a, b) => sortByDisplayOrder(a.day as number, b.day as number));

  for (const wh of orderedByDay) {
    const frame = wh.timeFrames?.find(
      (tf): tf is ScheduledTimeFrame =>
        !!tf && typeof tf.start === 'string' && typeof tf.end === 'string'
    );
    if (frame) {
      return frame;
    }
  }

  return null;
};

/**
 * Formats a ScheduledTimeFrame into a human-readable range string such as
 * "9:00 am - 5:00 pm EST". Returns an empty string when the frame is null or
 * the start/end values are not valid 24h clock strings.
 */
export const formatWorkingHoursRange = (
  frame: ScheduledTimeFrame | null,
  timeZone: string | null | undefined
): string => {
  if (!frame) {
    return '';
  }
  const start = moment(frame.start, CLOCK_INPUT_FORMATS, true);
  const end = moment(frame.end, CLOCK_INPUT_FORMATS, true);
  if (!start.isValid() || !end.isValid()) {
    return '';
  }
  const range = `${start.format('h:mm a')} - ${end.format('h:mm a')}`;
  const abbreviation =
    timeZone && moment.tz.zone(timeZone) ? moment.tz(timeZone).zoneAbbr() : '';
  return abbreviation ? `${range} ${abbreviation}` : range;
};
